import React, { createContext, useContext, useMemo } from 'react'
import { t } from 'i18next'
import { useAuth } from '@/contexts/AuthContext'

export type FlowKind =
  | 'organization'
  | 'municipality'
  | 'partner'
  | 'unknown'
  | 'admin'
  | 'admin_prelim'

export type FlowCapabilities = {
  canManageEntity?: boolean
  canManageUsers?: boolean
}

type FlowContextValue = {
  flow: FlowKind
  capabilities: FlowCapabilities
  isAdmin: boolean
  flowId?: string
  availableRolesOptions: { label: string; value: string }[]
}

const FlowContext = createContext<FlowContextValue>({
  flow: 'unknown',
  capabilities: {},
  isAdmin: false,
  flowId: '',
  availableRolesOptions: [],
})

export function FlowProvider({ children }: { children: React.ReactNode }) {
  const { me } = useAuth()

  const roles = me?.roles ?? []
  const flow: FlowKind = useMemo(() => {
    if (roles.some((r: string) => r.includes('org'))) {
      return 'organization'
    }
    if (roles.some((r: string) => r.includes('municipality'))) {
      return 'municipality'
    }
    if (roles.some((r: string) => r.includes('partner'))) {
      return 'partner'
    }
    if (roles.some((r: string) => r === 'admin_full')) {
      return 'admin'
    }
    if (roles.some((r: string) => r === 'admin_prelim')) {
      return 'admin_prelim'
    }
    return 'unknown'
  }, [me?.roles])

  const isAdmin = (me?.roles ?? []).some((r: string) => r.includes('admin'))
  const flowId = me?.scopeId

  const capabilities: FlowCapabilities = useMemo(() => {
    switch (flow) {
      case 'organization':
        return {
          canManageEntity: roles.some((r: string) => ['org_user', 'org_user_admin'].includes(r)),
          canManageUsers: roles.some((r: string) => ['org_user', 'org_user_admin'].includes(r)),
        }
      case 'municipality':
        return {
          canManageEntity: roles.some((r: string) =>
            ['municipality_user', 'municipality_user_admin'].includes(r)
          ),
          canManageUsers: roles.some((r: string) => ['municipality_user_admin'].includes(r)),
        }
      case 'partner':
        return {
          canManageEntity: false,
          canManageUsers: roles.some((r: string) =>
            ['partner_user', 'partner_user_admin'].includes(r)
          ),
        }
      case 'admin':
        return {
          canManageEntity: true,
          canManageUsers: true,
        }
      case 'admin_prelim':
        return {
          canManageEntity: false,
          canManageUsers: false,
        }
      default:
        return {}
    }
  }, [flow, isAdmin])

  const scopeRaw = String(me?.scopeType ?? '').toLowerCase()
  const scopeToken = scopeRaw === 'organization' ? 'org' : scopeRaw // 'org' | 'municipality' | 'partner'

  const availableRolesOptions = scopeToken
    ? [
        ...new Set(
          (me?.permissions?.invite ?? [])
            .filter((s: string) => s.includes('_')) // only role-like entries
            .map((s: string) => s.replace(/^manage:/, '')) // strip "manage:"
            .filter((r: string) => r.includes(`${scopeToken}_`)) // keep only current scope
        ),
      ].map((role) => ({
        value: role,
        label: t(`roles.${role}`),
      }))
    : []

  // Memoize the context value so consumers don't re-render on every
  // FlowProvider re-render (inline object literal was recreating every pass).
  const value = useMemo(
    () => ({ flow, capabilities, isAdmin, flowId, availableRolesOptions }),
    [flow, capabilities, isAdmin, flowId, availableRolesOptions]
  )

  return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>
}

export function useFlow() {
  return useContext(FlowContext)
}
